/**
 * Footer Manager utility for auto-managing Footer components on screens.
 * Handles automatic Footer creation and navigation target updates.
 */

import { generateId } from './idGeneration';

/**
 * Creates an auto-managed Footer component with appropriate navigation action.
 * @param {string|null} nextScreenId - The ID of the next screen to navigate to, or null for terminal screen
 * @returns {object} Footer component with auto-managed metadata
 */
export function createAutoManagedFooter(nextScreenId) {
  var action;
  var label;

  if (nextScreenId) {
    // Non-terminal screen: navigate to next screen
    action = {
      name: 'navigate',
      next: {
        type: 'screen',
        name: nextScreenId,
      },
    };
    label = 'Continue';
  } else {
    // Terminal screen: complete the flow
    action = {
      name: 'complete',
      payload: {},
    };
    label = 'Submit';
  }

  return {
    id: generateId(),
    type: 'Footer',
    properties: {
      label,
      'on-click-action': action,
    },
    metadata: {
      autoManaged: true,
    },
  };
}

/**
 * Checks if a screen has any Footer component.
 * @param {object} screen - The screen to check
 * @returns {boolean} True if the screen has a Footer
 */
export function hasFooter(screen) {
  return screen.components.some(comp => comp.type === 'Footer');
}

export function getFooterIndex(screen) {
  return screen.components.findIndex(comp => comp.type === 'Footer');
}

/**
 * Checks if a screen has an auto-managed Footer component.
 * @param {object} screen - The screen to check
 * @returns {boolean} True if the screen has an auto-managed Footer
 */
export function hasAutoManagedFooter(screen) {
  return screen.components.some(
    comp => comp.type === 'Footer' && comp.metadata?.autoManaged === true
  );
}

/**
 * Gets the auto-managed Footer component from a screen.
 * @param {object} screen - The screen to search
 * @returns {object|null} The auto-managed Footer or null
 */
export function getAutoManagedFooter(screen) {
  return screen.components.find(
    comp => comp.type === 'Footer' && comp.metadata?.autoManaged === true
  ) || null;
}

/**
 * Updates all auto-managed Footer navigation targets based on screen order.
 * Non-terminal screens navigate to the next screen, terminal screen completes.
 * @param {array} screens - Array of all screens in order
 * @returns {array} Updated screens array
 */
/**
 * Where an auto-managed Footer sends the customer: the next screen in the list, or nowhere
 * (complete) for the last screen and for any screen flagged `terminal`. A branching flow has
 * several endings ("we cannot help with that", "no, not now") that are not the last screen in
 * the list; until 2026-09-17 those compiled to a "Continue" into whatever screen happened to
 * follow, which for a multilingual flow was the next language's first screen.
 * @param {array} screens
 * @param {number} index
 * @returns {string|null}
 */
export function autoNextScreenId(screens, index) {
  var screen = screens[index];
  var isLastScreen = index === screens.length - 1;
  if (screen && screen.terminal === true) return null;
  // A routed default is where the customer actually goes; the list order is only the fallback.
  if (screen && screen.routing && screen.routing.defaultNextScreenId) return screen.routing.defaultNextScreenId;
  if (isLastScreen) return null;
  return screens[index + 1].id;
}

export function updateAllFooterNavigations(screens) {
  return screens.map((screen, index) => {
    var nextScreenId = autoNextScreenId(screens, index);

    var updatedComponents = screen.components.map(comp => {
      // Only update auto-managed Footers
      if (comp.type !== 'Footer' || !comp.metadata?.autoManaged) {
        return comp;
      }

      var action;
      var label;

      if (nextScreenId) {
        action = {
          name: 'navigate',
          next: {
            type: 'screen',
            name: nextScreenId,
          },
        };
        label = 'Continue';
      } else {
        action = {
          name: 'complete',
          payload: {},
        };
        label = 'Submit';
      }

      return {
        ...comp,
        properties: {
          ...comp.properties,
          label,
          'on-click-action': action,
        },
      };
    });

    return {
      ...screen,
      components: updatedComponents,
    };
  });
}

/**
 * Ensures all screens have appropriate Footer components.
 * - If screen already has a Footer (user-added), leaves it alone
 * - If screen has no Footer, adds an auto-managed one
 * - Updates all auto-managed Footer navigation targets
 * @param {array} screens - Array of all screens
 * @returns {array} Updated screens array with Footers
 */
export function ensureFootersOnAllScreens(screens) {
  // First pass: add missing Footers
  var screensWithFooters = screens.map((screen, index) => {
    if (hasFooter(screen)) {
      // Screen already has a Footer (user-added or auto-managed)
      return screen;
    }

    // Add auto-managed Footer
    var footer = createAutoManagedFooter(autoNextScreenId(screens, index));

    return {
      ...screen,
      components: [...screen.components, footer],
    };
  });

  // Second pass: update all auto-managed Footer navigations to ensure consistency
  return updateAllFooterNavigations(screensWithFooters);
}

/**
 * Re-chains the flow's terminal Footer onto a newly inserted last screen.
 *
 * When the user adds or copies a screen at the end of the flow, the screen that used to finish the
 * flow (its Footer's on-click-action is `complete`) should instead navigate to the new screen, so
 * the new screen becomes reachable and takes over as the terminal one.
 *
 * Unlike {@link updateAllFooterNavigations}, this runs for ALL Footers, not just auto-managed ones:
 * a Footer loaded from saved JSON or set by hand carries no `autoManaged` flag, which is exactly the
 * case the user hits (a flow opened from the list, then a screen added). It is a no-op unless the
 * new screen is the last screen (we only extend the end of the flow) and the predecessor currently
 * completes the flow (so a deliberate mid-flow navigation is never overridden).
 *
 * @param {array}  screens       - all screens in order, with the new screen already inserted
 * @param {string} newScreenId   - the screen that should become the new terminal
 * @param {string} predecessorId - the screen that should now navigate to the new screen
 * @returns {array} updated screens
 */
export function chainTerminalToNewScreen(screens, newScreenId, predecessorId) {
  var newIndex = screens.findIndex(s => s.id === newScreenId);
  if (newIndex !== screens.length - 1) {
    return screens; // only extend the end of the flow
  }
  var predecessor = screens.find(s => s.id === predecessorId);
  var predFooter = predecessor && predecessor.components.find(c => c.type === 'Footer');
  if (predFooter?.properties?.['on-click-action']?.name !== 'complete') {
    return screens; // predecessor wasn't terminal, nothing to swap (auto-managed cases already chained)
  }

  return screens.map(screen => {
    // The new screen takes over as the terminal one. Its Footer already completes (it is the last
    // screen), so this just keeps the persisted `terminal` flag consistent with that.
    if (screen.id === newScreenId) {
      return { ...screen, terminal: true };
    }
    if (screen.id !== predecessorId) {
      return screen;
    }
    return {
      // No longer terminal: it now navigates onward. Clear the flag too, it is persisted and
      // OR'd into terminal detection at export, so leaving it true would mark a navigating screen
      // terminal.
      ...screen,
      terminal: false,
      components: screen.components.map(comp => {
        if (comp.type !== 'Footer') {
          return comp;
        }
        var label = comp.properties?.label;
        return {
          ...comp,
          properties: {
            ...comp.properties,
            // Keep a custom label; only swap the stock terminal label for the stock continue one.
            label: (!label || label === 'Submit') ? 'Continue' : label,
            'on-click-action': {
              name: 'navigate',
              next: { type: 'screen', name: newScreenId },
            },
          },
        };
      }),
    };
  });
}

/**
 * When a screen is deleted, fix up the Footer(s) that navigated to it.
 *
 * Rule (per product intent): if **exactly one** remaining screen's Footer navigates to the deleted
 * screen, that Footer transitions from `navigate` to `complete` (the flow now ends there). If
 * **zero or multiple** screens pointed at it, do nothing, leave it for the user to resolve, since
 * auto-completing several footers (or guessing a new target) would be presumptuous.
 *
 * Only Footer `navigate` actions are considered (not NavigationList items or data_exchange).
 *
 * @param {array}  screens         - the remaining screens (the deleted one already removed)
 * @param {string} deletedScreenId - id of the screen that was just removed
 * @returns {array} screens with the sole pointer completed, or unchanged
 */
export function completeSolePointerToDeletedScreen(screens, deletedScreenId) {
  var navigatesToDeleted = (screen) => {
    var footer = screen.components.find((c) => c.type === 'Footer');
    var action = footer?.properties?.['on-click-action'];
    return action?.name === 'navigate' && action?.next?.name === deletedScreenId;
  };

  var pointers = screens.filter(navigatesToDeleted);
  if (pointers.length !== 1) {
    return screens; // 0 or multiple, leave it for the user
  }

  var soleId = pointers[0].id;
  return screens.map((screen) => {
    if (screen.id !== soleId) {
      return screen;
    }
    return {
      ...screen,
      terminal: true,
      components: screen.components.map((comp) => {
        if (comp.type !== 'Footer') {
          return comp;
        }
        var label = comp.properties?.label;
        return {
          ...comp,
          properties: {
            ...comp.properties,
            // Its target is gone, so it completes the flow. Swap the stock "Continue" label for the
            // stock "Submit"; keep a custom one.
            label: (!label || label === 'Continue') ? 'Submit' : label,
            'on-click-action': { name: 'complete', payload: {} },
          },
        };
      }),
    };
  });
}

/**
 * Returns true when a screen has conditional routing rules defined.
 * Used by JSON generation to know the Footer will be If-wrapped.
 * @param {object} screen
 * @returns {boolean}
 */
export function screenHasRoutingConditions(screen) {
  return (
    Array.isArray(screen.routing?.rules) &&
    screen.routing.rules.length > 0
  );
}

